import { useState } from 'react';

import { InfoModal, AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  ProfileValueOptionResponse,
  ProfileFields,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Typography, SxProps, Theme, Stack, TypographyProps } from '@mui/material';

import { ProfileEditSectionItem } from './ProfileEditSectionItem';
import { ProfileFieldConfig, ProfileSectionConfig } from './types/profile.types';

const PROFILE_EDIT_SECTION_STRINGS = appStrings.modules.profile.edit;

type InfoModalData = Readonly<{
  title: string;
  description: string;
}>;

const InfoModalContent = ({ title, description }: InfoModalData) => (
  <Box sx={headerContentContainerSx}>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Gray/900']}
      variant="text-lg-semibold"
    >
      {title}
    </Typography>
    <Typography
      color={(theme: Theme) => theme.backrs.colors['Colors/Base/Black']}
      variant="text-sm-regular"
    >
      {description}
    </Typography>
  </Box>
);

export type ProfileEditSectionProps = Readonly<{
  title: string;
  titleFontVariant?: TypographyProps['variant'];
  description: string;
  infoModal?: {
    title: string;
    description: string;
  };
  fields: ProfileFieldConfig[];
  profileOptions: Record<string, ProfileValueOptionResponse[ProfileFields]>;
  isPrivateField: boolean;
  viewExamples?: ProfileSectionConfig['viewExamples'];
  hideDivider: boolean;
}>;

export const ProfileEditSection = ({
  title,
  titleFontVariant = 'text-sm-semibold',
  description,
  infoModal,
  fields,
  profileOptions,
  isPrivateField,
  hideDivider,
  viewExamples,
}: ProfileEditSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Box sx={(theme: Theme) => containerSx(theme, hideDivider)}>
      {viewExamples?.content(isModalOpen, setIsModalOpen)}

      <Box sx={sectionInfoSx}>
        <Stack flexDirection="row">
          <Typography
            variant={titleFontVariant}
            color={(theme: Theme) => theme.backrs.colors['Gray/700']}
          >
            {title}
          </Typography>
          {infoModal && (
            <>
              <Box component="span" onClick={() => setIsModalOpen(true)} sx={modalIconSx}>
                <AppIcon iconName="HelpCircle" diameter="16px" color="Gray/400" />
              </Box>
              <InfoModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                dialogSx={dialogSx}
                bodyContent={
                  <InfoModalContent title={infoModal.title} description={infoModal.description} />
                }
              />
            </>
          )}
        </Stack>
        <Stack>
          <Typography
            variant="text-sm-regular"
            color={(theme: Theme) => theme.backrs.colors['Gray/600']}
          >
            {description}
          </Typography>
          {isPrivateField && (
            <Typography
              variant="text-sm-regular"
              color={(theme: Theme) => theme.backrs.colors['Gray/600']}
            >
              {PROFILE_EDIT_SECTION_STRINGS.privateField}
            </Typography>
          )}
          {viewExamples && (
            <Typography
              variant="text-sm-semibold"
              sx={viewExamplesSx}
              onClick={() => setIsModalOpen(true)}
            >
              {viewExamples.label}
            </Typography>
          )}
        </Stack>
      </Box>

      <Box sx={inputContainerSx}>
        {(() => {
          // Group fields: consecutive half-width fields together, full-width fields alone
          const groups = fields.reduce<ProfileFieldConfig[][]>((acc, field, i, arr) => {
            // Skip if we've already processed this field
            if (i > 0 && acc.flat().includes(field)) {
              return acc;
            }

            // If current field is half width and next field exists and is also half width
            if (
              field.width === 'half' &&
              arr[i + 1]?.width === 'half' &&
              !acc.flat().includes(arr[i + 1])
            ) {
              return [...acc, [field, arr[i + 1]]];
            }

            return [...acc, [field]];
          }, []);

          return groups.map((group) => (
            <Box key={group.map((f) => f.key).join('-')} sx={groupContainerSx(group.length)}>
              {group.map((field) => (
                <Box
                  key={field.key}
                  sx={{
                    width: { sm: field.width === 'half' ? 'calc(50% - 8px)' : '100%' },
                  }}
                >
                  <ProfileEditSectionItem
                    key={field.key}
                    field={field}
                    options={profileOptions?.[field.key] ?? []}
                  />
                </Box>
              ))}
            </Box>
          ));
        })()}
      </Box>
    </Box>
  );
};

const viewExamplesSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  cursor: 'pointer',
  marginTop: backrs.spacing['spacing-lg'],
});

const groupContainerSx = (groupLength: number) => ({
  display: 'flex',
  gap: { xs: 0, sm: 2 },
  flexWrap: 'wrap',
  width: '100%',
  flexDirection: { xs: 'column', sm: groupLength === 2 ? 'row' : 'column' },
});

const sectionInfoSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '280px',
  maxWidth: '280px',
};

const inputContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: { xs: '100%', sm: '512px' },
  maxWidth: { xs: '100%', sm: '512px' },
  flexWrap: 'wrap',
  gap: { xs: backrs.spacing['spacing-none'], sm: backrs.spacing['spacing-md'] },
});

const modalIconSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: backrs.spacing['spacing-xxs'],
});

const dialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': { maxWidth: '343px' },
};

const containerSx = (theme: Theme, hideDivider: boolean) => ({
  borderTop: !hideDivider ? `1px solid ${theme.backrs.colors['Gray/200']}` : 'none',
  paddingTop: !hideDivider ? theme.backrs.spacing['spacing-2xl'] : 'inherit',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: 'flex-start',
  gap: `${theme.backrs.spacing['spacing-xl']} ${theme.backrs.spacing['spacing-4xl']}`,
});

const headerContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xxs'],
});
